import { Calendar, Clock, Users, Palette, Code, BarChart3 } from "lucide-react";
import { DoodleGear, DoodleDatabase, DoodleSignal, FloatingDoodle } from "./Doodles";
import { useInViewOnce } from "@/hooks/useInViewOnce";
import { useLanguage } from "@/contexts/LanguageContext";

const FeaturesSection = () => {
  const { ref, isInView } = useInViewOnce();
  const { t } = useLanguage();

  const features = [
    { icon: Calendar, title: t("landing.feat_calendar"), description: t("landing.feat_calendar_desc") },
    { icon: Clock, title: t("landing.feat_timeslots"), description: t("landing.feat_timeslots_desc") },
    { icon: Users, title: t("landing.feat_customers"), description: t("landing.feat_customers_desc") },
    { icon: Palette, title: t("landing.feat_shop"), description: t("landing.feat_shop_desc") },
    { icon: Code, title: t("landing.feat_widget"), description: t("landing.feat_widget_desc") },
    { icon: BarChart3, title: t("landing.feat_services"), description: t("landing.feat_services_desc") },
  ];

  return (
    <section id="features" className="relative py-24 bg-background overflow-hidden">
      {/* Doodle decorations */}
      <div className="pointer-events-none absolute inset-0">
        <FloatingDoodle delay={0.5} duration={5}>
          <DoodleGear className="absolute top-12 right-[8%] text-primary/15" />
        </FloatingDoodle>
        <FloatingDoodle delay={1} duration={4} y={6}>
          <DoodleDatabase className="absolute bottom-20 left-[5%] text-accent/15" />
        </FloatingDoodle>
        <FloatingDoodle delay={2} duration={6}>
          <DoodleSignal className="absolute top-1/2 right-[3%] text-primary/10" />
        </FloatingDoodle>
      </div>

      <div className="container relative mx-auto px-4">
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-sm font-semibold uppercase tracking-wider text-primary">{t("landing.features_label")}</span>
          <h2 className="mt-3 text-4xl font-bold text-foreground">
            {t("landing.features_title")}
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            {t("landing.features_desc")}
          </p>
        </div>

        <div ref={ref} className="mx-auto mt-16 grid max-w-6xl gap-8 md:grid-cols-2 lg:grid-cols-3">
          {features.map((feature, i) => (
            <div
              key={i}
              className="group rounded-xl border border-border bg-card p-6 shadow-card transition-all duration-300 hover:shadow-card-hover hover:-translate-y-1"
              style={{
                opacity: isInView ? 1 : 0,
                transform: isInView ? "translateY(0)" : "translateY(20px)",
                transition: `opacity 0.4s ease-out ${i * 0.08}s, transform 0.4s ease-out ${i * 0.08}s`,
              }}
            >
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary transition-colors group-hover:bg-gradient-primary group-hover:text-primary-foreground">
                <feature.icon className="h-6 w-6" />
              </div>
              <h3 className="text-lg font-semibold text-card-foreground">{feature.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
